import { useQuery } from 'urql';

import {
  ContentType,
  GetReferralInviteContentDocument,
  GetReferralInviteContentQuery,
  GetReferralInviteDocument,
  GetReferralInviteQuery,
  OrganisationContent,
  ReferralInvite,
} from '../generated/referral';

const INTERNAL_SERVER_ERROR = 500;
const INTERNAL_SERVER_ERROR_MESSAGE = 'Internal server error';

type QueryError = {
  status: number;
  message: string;
};

type UseReferralInviteReturn = ReturnType<typeof useQuery<GetReferralInviteQuery>>;

const useReferralInviteQuery = (
  invitePublicId?: string
): [ReferralInvite | undefined, boolean, QueryError | undefined, UseReferralInviteReturn[1]] => {
  const [{ data, fetching, error: combinedError }, reexecuteQuery] = useQuery<GetReferralInviteQuery>({
    query: GetReferralInviteDocument,
    variables: {
      invitePublicId,
    },
    requestPolicy: 'cache-and-network',
    pause: !invitePublicId?.trim(),
  });
  const result = data?.getReferralInvite ?? undefined;

  const graphQLError = combinedError?.graphQLErrors?.[0];

  const error = graphQLError
    ? {
        status: Number(graphQLError.extensions['status'] ?? INTERNAL_SERVER_ERROR),
        message: graphQLError.message ?? INTERNAL_SERVER_ERROR_MESSAGE,
      }
    : undefined;

  return [result, fetching, error, reexecuteQuery];
};

type UseReferralInviteContentReturn = ReturnType<typeof useQuery<GetReferralInviteContentQuery>>;

const useReferralInviteContentQuery = (
  contentType: ContentType,
  invitePublicId?: string
): [OrganisationContent | undefined, boolean, QueryError | undefined, UseReferralInviteContentReturn[1]] => {
  const [{ data, fetching, error: combinedError }, reexecuteQuery] = useQuery<GetReferralInviteContentQuery>({
    query: GetReferralInviteContentDocument,
    variables: {
      contentType,
      invitePublicId,
    },
    pause: !invitePublicId?.trim(),
  });
  const result = data?.getReferralInviteContent ?? undefined;

  const graphQLError = combinedError?.graphQLErrors?.[0];

  const error = graphQLError
    ? {
        status: Number(graphQLError.extensions['status'] ?? INTERNAL_SERVER_ERROR),
        message: graphQLError.message ?? INTERNAL_SERVER_ERROR_MESSAGE,
      }
    : undefined;

  return [result, fetching, error, reexecuteQuery];
};

export { type QueryError, useReferralInviteContentQuery, useReferralInviteQuery };
